import { addProductToCart, getCart, getProduct, refreshCart } from "api/shop";
import Accordion from "components/Accordion/Accordion";
import InsButton from "components/InsButton/InsButton";
import InsLoader from "components/InsLoader/InsLoader";
import InsSelect from "components/InsSelect/InsSelect";
import AddToCartModal from "components/Shop/Product/AddToCartModal";
import Variations from "components/Shop/Product/Variations";
import React, { useEffect, useState, useCallback } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useSelector } from "react-redux";
import { Link, Redirect, useRouteMatch } from "react-router-dom";
import images from "ressources/images";
import "./ShopProduct.scss";

const ShopProduct = () => {
  const { t, i18n } = useTranslation();
  const account = useSelector((state) => state.account);
  const [loading, setloading] = useState(true);
  const [product, setproduct] = useState();
  const [cart, setcart] = useState();
  const [error, seterror] = useState();
  const [success, setsuccess] = useState(false);
  const { params } = useRouteMatch();

  // form
  const [quantity, setquantity] = useState(1);
  const [variation, setvariation] = useState();
  const [openVariations, setopenVariations] = useState();

  const stopLoading = () => setloading(false);
  const startLoading = () => setloading(true);

  const fetchInformations = async (id) => {
    startLoading();
    const productPromise = getProduct(id);
    const cartPromise = getCart(id);

    Promise.all([productPromise, cartPromise])
      .then(([product, cart]) => {
        setproduct(product);
        setcart(cart);

        // By default set the first variation
        if (product.variations.length > 0) {
          setvariation(product.variations[0]);
        } else {
          // If no variation is available, use the default product data instead
          setvariation(product);
        }
      })
      .then(stopLoading)
      .catch((err) => {
        // TODO redirect
        seterror(true);
      });
  };

  const addToCart = () => {
    startLoading();

    addProductToCart(variation.ean, quantity).then(() => {
      stopLoading();
      setsuccess(true);
      refreshCart();
    });
  };

  const toggleVariation = () => {
    setopenVariations((state) => !state);
  };

  const handleVariationSelection = (index) => {
    setvariation(product.variations[index]);
    setopenVariations(false);
  };

  const handleQuantitySelection = (event) => {
    setquantity(event.target.value);
  };

  const getSelectedProductQuantityFromCart = useCallback(() => {
    const productItem = cart?.elements?.find((item) => item.id === product.id);
    return productItem ? productItem.quantity : 0;
  }, [cart, product]);

  const getQuantityOptions = (returnQuantityValue = false) => {
    const { max_qty = null, qty_ordered = null } = product ?? {};
    let maxQuantity = variation.stock || 0;
    const productQuantityInCart = getSelectedProductQuantityFromCart();
    if (variation.points > 0) {
      const expectedUserPoints = account.points - cart.total;
      const cartQuantityTotal = variation.stock - productQuantityInCart;
      maxQuantity = Math.min(
        cartQuantityTotal,
        maxQuantity,
        Math.floor(expectedUserPoints / variation.points)
      );
    }
    
    if (max_qty) {
      let maxOrders = qty_ordered ? max_qty - qty_ordered : max_qty;
      maxOrders = maxOrders - productQuantityInCart;
      maxQuantity = maxQuantity > maxOrders ? maxOrders : maxQuantity;
    }

    if (returnQuantityValue) {
      return maxQuantity;
    }

    if(maxQuantity < 0) {
      return [];
    }

    return Array(maxQuantity)
      .fill(1)
      .map((x, y) => {
        const value = (y + 1).toString();
        return { value, label: value };
      })
  };

  const canAddToCart = () => {
    const { max_qty = null, qty_ordered = null } = product ?? {};

    if (!variation || loading) {
      return false;
    }

    if (variation.stock < 1) {
      return false;
    }

    // We take into account the cost of everything that is already in the cart
    const expectedUserPoints = account.points - cart.total;
    const cost = quantity * variation.points;
    const productQuantityInCart = getSelectedProductQuantityFromCart();
    const cartQuantityTotal = variation.stock - productQuantityInCart;

    if (expectedUserPoints < cost || cartQuantityTotal < quantity) {
      return false;
    }

    if (max_qty) {
      const productQuantityInCart = getSelectedProductQuantityFromCart();
      const maxQuantity = qty_ordered ? max_qty - qty_ordered : max_qty;
      return maxQuantity - productQuantityInCart; 
    }

    return true;
  };

  const hasVariations = product?.variations?.length > 0;

  useEffect(() => {
    fetchInformations(params.id);

    return () => {};
  }, [params.id, i18n.language]);

  useEffect(() => {
    setquantity(1);
  }, [variation]);

  const showProductLimitMessage = useCallback(() => {
    const { max_qty = null, qty_ordered = null } = product ?? {};
    const productQuantityInCart = getSelectedProductQuantityFromCart();
    const qtyOrderedWithCart = qty_ordered + productQuantityInCart;
    const maxOrderableQuantity = variation?.stock ? getQuantityOptions(true) : 0;

    if (!max_qty) {
      return null;
    } else if (!qtyOrderedWithCart) {
      if (maxOrderableQuantity < max_qty) {
        return (
          <Trans
            i18nKey="product_order_limit_upto_x_items"
            defaults="Up to {{limitation}} items available to order"
            values={{ limitation: maxOrderableQuantity }}
          />
        )
      }

      return (
        <Trans
          i18nKey="product_order_limit"
          defaults="This product is limited to {{limitation}} items"
          values={{ limitation: max_qty }}
        />
      );
    } else if (qtyOrderedWithCart < max_qty) {
      const limitationValue = max_qty - qtyOrderedWithCart;
      if (maxOrderableQuantity === 0) {
        return (
          <Trans
            i18nKey="product_order_limit_already_ordered_available_stock"
            defaults="Already added all available stock to cart"
          />
        )
      }
      else if (maxOrderableQuantity < limitationValue) {
        return (
          <Trans
            i18nKey="product_order_limit_upto_x_items"
            defaults="Up to {{limitation}} items available to order"
            values={{ limitation: maxOrderableQuantity }}
          />
        )
      }
      return (
        <Trans
          i18nKey="product_order_left"
          defaults="{{limitation}} items left to order from this product"
          values={{ limitation: limitationValue }}
        />
      );
    } else if (qtyOrderedWithCart >= max_qty) {
      return (
        <Trans
          i18nKey="product_order_limit_reached"
          defaults="Product order limit exceeded ({{limitation}} items)"
          values={{ limitation: max_qty }}
        />
      );
    }
  }, [cart, product, variation]);

  if (error) {
    return <Redirect to="/shop" />;
  }

  if (loading) {
    return (
      <div className="product_page default-layout--min-height">
        <InsLoader />
      </div>
    );
  }

  if (openVariations) {
    return (
      <div className="product_page default-layout--min-height">
        <Variations
          variations={product.variations}
          variationType={product.variationType}
          onVariationSelection={handleVariationSelection}
          onClose={toggleVariation}
        />
      </div>
    );
  }

  return (
    <div className="product_page default-layout--min-height">
      <Link
        to={{
          pathname: "/shop",
          state: { transitionName: "slide_out" },
        }}
        className="back_link"
      />

      <div className="product_infos">
        <div className="product_brand">
          <img src={product.brand.logoNoir} alt="" />
        </div>

        <div
          className={"product_price" + (variation.stock > 0 ? "" : " disabled")}
        >
          {variation.points}
        </div>

        <div className="product_picture">
          <img
            src={
              product.pictures.length
                ? product.pictures[0].name.name_333
                : images.we_are_lol_black
            }
            alt={product.name}
          />
        </div>

        <div className="product_name_wrapper">
          <div className="product_name product_name-bold">{product.name}</div>
          <div className="product_variations">
            <div className="container">
              {variation.value_label}
              {product.variationType === "capacity" && " ml"}
            </div>
          </div>
        </div>

        <Accordion label={t("product_desc")}>
          <div dangerouslySetInnerHTML={{ __html: product.description }} />
        </Accordion>
      </div>

      <div className="select_wrapper">
        {hasVariations && (
          <>
            <div className="select_block">
              <div className="select_variations" onClick={toggleVariation}>
                {product.variationType === "color" ? (
                  <div className="selected">
                    <div
                      className="color"
                      style={{ backgroundColor: variation.color }}
                    ></div>
                    <div className="label">{variation.value_label}</div>
                  </div>
                ) : (
                  <div className="selected container">
                    <div className="label">{t("container")}</div>
                    <div className="value">{variation.value_label}</div>
                  </div>
                )}
              </div>
            </div>
            {/* {openVariations && (
              <Variations
                variations={product.variations}
                variationType={product.variationType}
                onVariationSelection={handleVariationSelection}
                onClose={toggleVariation}
              />
            )} */}
          </>
        )}

        {variation.stock > 0 && (
          <>
            <div className="select_block qty">
              <div className="qty_holder">{t("quantity")}</div>
              <InsSelect
                className="qty_select"
                name="qty"
                onChange={handleQuantitySelection}
                values={getQuantityOptions()}
                dir="rtl"
              />
            </div>
            <div className="product_info">{showProductLimitMessage()}</div>
          </>
        )}
      </div>

      <p className="terms-and-conditions">{t("shop_terms_conditions")}</p>

      <InsButton
        onClick={addToCart}
        type="outline"
        disabled={!canAddToCart()}
        text={t("add_to_cart")}
      />
      {success && <AddToCartModal />}
    </div>
  );
};

export default ShopProduct;
